import { Box } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { usePreferencesRead } from '~/backend/connector-next-api/connector-next-components';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';
import {
    resolveStartupView,
    type StartupView,
} from '~/hooks/workspace/startupView';
import { useAssignedToMe, usePatientListState } from './hooks';
import PatientListScreen from './PatientListScreen';
import PatientListTabs, { type PatientListView } from './PatientListTabs';
import TaskListView from './task-list/TaskListView';

/** The view each startup preference opens on. */
const STARTUP: Record<StartupView, PatientListView> = {
    MyTasks: 'tasks',
    AllPatients: 'all',
};

/**
 * The patient list's views: the patients assigned to the user, and every
 * patient. Opens on the user's startup view until they pick one; where the
 * connector identifies nobody, All patients is the only view.
 */
export default function PatientListViews() {
    const { signInEnabled } = useConnectorIdentity();
    const { data: preferences, isError: preferencesFailed } =
        usePreferencesRead({});
    const [picked, setView] = useState<PatientListView>();
    // No view is shown until the stored one is known, so a list never opens
    // only to be replaced. A failed read opens on the default.
    const awaitingStartupView =
        signInEnabled &&
        picked === undefined &&
        preferences === undefined &&
        !preferencesFailed;
    const allPatients = usePatientListState();
    const view = signInEnabled
        ? (picked ?? STARTUP[resolveStartupView(preferences?.startupView)])
        : 'all';
    // My patients loads only while its view is open; its tab is counted by the
    // connector.
    const assignedToMe = useAssignedToMe(view === 'tasks');
    const tabs = (
        <PatientListTabs
            view={view}
            onChange={setView}
            focusSelected={picked !== undefined}
        />
    );
    const views: Record<PatientListView, ReactNode> = {
        tasks: <TaskListView list={assignedToMe} tabs={tabs} />,
        all: <PatientListScreen tabs={tabs} state={allPatients} />,
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                bgcolor: 'background.sunken',
                overflow: 'hidden',
            }}
        >
            {!awaitingStartupView && views[view]}
        </Box>
    );
}
